import {
  installTemplateSteps,
  type AdminGame,
  type AdminGameVersion,
  type InstallerReport,
  type InstallTemplate,
} from '@mcgames/contracts';
import { Button, Dialog, Select, TextInput } from '@mcgames/ui';
import { useId, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiQuery } from '../../api/hooks.ts';
import { ErrorText } from './ErrorText.tsx';
import { gamePath } from './game-hooks.ts';
import styles from './Games.module.css';
import {
  STEP_TYPES,
  installDraftFromVersion,
  installRequest,
  newStepDraft,
  stepToDraft,
  type InstallDraft,
  type InstallRequest,
  type StepDraft,
  type StepType,
} from './install-draft.ts';
import { InstallerReports } from './InstallerReports.tsx';
import { StepEditor } from './StepEditor.tsx';
import { useVersionMutation } from './version-hooks.ts';

const TEMPLATES: readonly InstallTemplate[] = ['repack_inno', 'iso_setup', 'portable'];
const PLACEHOLDERS = ['INSTALL_DIR', 'EXTRACT_DIR', 'STAGING_DIR', 'PREFIX'] as const;

export type VersionInstallFormProps = {
  game: AdminGame;
  version: AdminGameVersion;
  /**
   * The unsaved edits of this version, owned by the editor so they survive a switch to another
   * tab or version; `null` when there are none.
   */
  draft: InstallDraft | null;
  onDraftChange: (draft: InstallDraft | null) => void;
};

/** The install steps, archive password and fixed executable of one version. */
export function VersionInstallForm({
  game,
  version,
  draft,
  onDraftChange,
}: VersionInstallFormProps) {
  const { t } = useTranslation('admin');
  const placeholdersId = useId();
  const titleId = useId();
  const save = useVersionMutation<InstallRequest>(game.id, version.id, 'PATCH');
  const saved = useMemo(() => installDraftFromVersion(version), [version]);
  const installerReports = useApiQuery<InstallerReport[]>(`${gamePath(game.id)}/installer-reports`);
  // Reports belong to a saved `run_installer` step of this version that still has the glob that
  // matched nothing.
  const reportsByStep = useMemo(() => {
    const byKey = new Map<number, InstallerReport[]>();
    for (const report of installerReports.data ?? []) {
      if (report.versionId !== version.id) continue;
      const step = version.installSteps[report.step];
      const draft = saved.steps[report.step];
      if (step?.type !== 'run_installer' || step.path !== report.pattern || draft === undefined) {
        continue;
      }
      byKey.set(draft.key, [...(byKey.get(draft.key) ?? []), report]);
    }
    return byKey;
  }, [installerReports.data, version.id, version.installSteps, saved]);
  const current = draft ?? saved;
  const dirty =
    draft !== null &&
    JSON.stringify(installRequest(draft)) !== JSON.stringify(installRequest(saved));
  const [template, setTemplate] = useState<InstallTemplate | null>(null);
  const [newType, setNewType] = useState<StepType>('extract');

  function applyTemplate(name: InstallTemplate) {
    onDraftChange({ ...current, steps: installTemplateSteps(name).map(stepToDraft) });
  }

  function chooseTemplate(name: InstallTemplate | '') {
    if (name === '') return;
    if (current.steps.length === 0) applyTemplate(name);
    else setTemplate(name);
  }

  function setSteps(steps: StepDraft[]) {
    onDraftChange({ ...current, steps });
  }

  function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    const steps = [...current.steps];
    const [step, other] = [steps[index], steps[target]];
    if (step === undefined || other === undefined) return;
    steps[index] = other;
    steps[target] = step;
    setSteps(steps);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate(installRequest(current), { onSuccess: () => onDraftChange(null) });
  }

  return (
    <form className={styles['form']} aria-labelledby={titleId} onSubmit={submit}>
      <h3 id={titleId} className={styles['subheading']}>
        {t('games.install.title')}
      </h3>
      <p className={styles['muted']}>{t('games.install.intro')}</p>
      <Select<InstallTemplate | ''>
        label={t('games.install.applyTemplate')}
        value=""
        options={[
          { value: '', label: t('games.install.templatePlaceholder') },
          ...TEMPLATES.map((name) => ({
            value: name,
            label: t(`games.install.templates.${name}`),
          })),
        ]}
        onChange={chooseTemplate}
      />
      <div role="group" aria-labelledby={placeholdersId} className={styles['placeholders']}>
        <h4 id={placeholdersId} className={styles['subheading']}>
          {t('games.install.placeholders')}
        </h4>
        <p className={styles['hint']}>{t('games.install.placeholdersHint')}</p>
        <dl className={styles['facts']}>
          {PLACEHOLDERS.map((name) => (
            <PlaceholderRow key={name} name={name} />
          ))}
        </dl>
      </div>
      <h4 className={styles['subheading']}>{t('games.install.steps')}</h4>
      {current.steps.length === 0 ? (
        <p className={styles['muted']}>{t('games.install.noSteps')}</p>
      ) : (
        <ol className={styles['steps']}>
          {current.steps.map((step, index) => {
            const reports = step.type === 'run_installer' ? reportsByStep.get(step.key) : undefined;
            const change = (next: StepDraft) =>
              setSteps(current.steps.map((s) => (s === step ? next : s)));
            return (
              <li key={step.key}>
                <StepEditor
                  step={step}
                  position={index + 1}
                  count={current.steps.length}
                  onChange={change}
                  onMove={(direction) => move(index, direction)}
                  onRemove={() => setSteps(current.steps.filter((s) => s !== step))}
                />
                {reports !== undefined && (
                  <InstallerReports
                    reports={reports}
                    currentPath={step.path}
                    onUse={(path) => change({ ...step, path })}
                  />
                )}
              </li>
            );
          })}
        </ol>
      )}
      <div className={styles['row']}>
        <Select<StepType>
          label={t('games.install.stepType')}
          value={newType}
          options={STEP_TYPES.map((type) => ({
            value: type,
            label: t(`games.install.kinds.${type}`),
          }))}
          onChange={setNewType}
        />
        <Button
          variant="secondary"
          onClick={() => setSteps([...current.steps, newStepDraft(newType)])}
        >
          {t('games.install.addStep')}
        </Button>
      </div>
      <TextInput
        label={t('games.install.archivePassword')}
        value={current.archivePassword}
        autoComplete="off"
        spellCheck={false}
        onChange={(event) => onDraftChange({ ...current, archivePassword: event.target.value })}
      />
      <span className={styles['hint']}>
        {version.nzbHasPassword
          ? t('games.install.archivePasswordFromNzb')
          : t('games.install.archivePasswordHint')}
      </span>
      <TextInput
        label={t('games.install.exe')}
        value={current.exe}
        spellCheck={false}
        onChange={(event) => onDraftChange({ ...current, exe: event.target.value })}
      />
      <span className={styles['hint']}>{t('games.install.exeHint')}</span>
      <ErrorText error={save.error} />
      <div className={styles['row']}>
        <Button type="submit" disabled={save.isPending}>
          {t('games.install.save')}
        </Button>
        <p role="status" className={dirty ? styles['hint'] : styles['status']}>
          {dirty
            ? t('games.install.unsaved')
            : save.isSuccess && !save.isPending
              ? t('games.install.saved')
              : ''}
        </p>
      </div>
      {template !== null && (
        <Dialog
          open
          onClose={() => setTemplate(null)}
          title={t('games.install.replaceTitle')}
          footer={
            <>
              <Button variant="secondary" onClick={() => setTemplate(null)}>
                {t('actions.cancel', { ns: 'common' })}
              </Button>
              <Button
                onClick={() => {
                  applyTemplate(template);
                  setTemplate(null);
                }}
              >
                {t('games.install.replaceConfirm')}
              </Button>
            </>
          }
        >
          <p>
            {t('games.install.replaceMessage', {
              template: t(`games.install.templates.${template}`),
            })}
          </p>
        </Dialog>
      )}
    </form>
  );
}

function PlaceholderRow({ name }: { name: (typeof PLACEHOLDERS)[number] }) {
  const { t } = useTranslation('admin');
  return (
    <>
      <dt>
        <code>{`{${name}}`}</code>
      </dt>
      <dd>{t(`games.install.placeholderHelp.${name}`)}</dd>
    </>
  );
}
